import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { SystemEntity } from '@wdx/clmi/clmi-swagger-gen';
import { operationsActions } from '@wdx/clmi/api-services/state';
import {
    DynamicFormsManagementService,
    ModalManagementService,
} from '@wdx/clmi/ui';
import { WORK_ITEM_FORM_ID } from '@wdx/shared/utils';
import {
    DynamicFormsFacade,
    FormFrameworkEvent,
    FormFrameworkEventType,
} from '@wdx/shared/infrastructure/form-framework';
import { merge, Observable, of, race } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { WorkItemDialogService } from '../../shared/features/work-item-comment';
import * as outcomesActions from './outcomes.actions';
import { OutcomesService } from './outcomes.service';

@Injectable()
export class OutcomesEffects {
    private actions$ = inject(Actions);
    private outcomesService = inject(OutcomesService);
    private workItemDialogService = inject(WorkItemDialogService);
    private dynamicFormsManagementService = inject(
        DynamicFormsManagementService,
    );
    private modalManagementService = inject(ModalManagementService);
    private dynamicFormsFacade = inject(DynamicFormsFacade);

    setOutcomeForWorkItem$ = createEffect(() =>
        this.actions$.pipe(
            ofType(outcomesActions.setOutcomeForWorkItem),
            switchMap((action) =>
                this.outcomesService
                    .setOutcomeForWorkItem(
                        action.outcomeCode,
                        action.workItemId,
                        action?.comment,
                        action?.formData,
                    )
                    .pipe(
                        map((response) =>
                            outcomesActions.setOutcomeForWorkItemSuccess({
                                outcomeCode: action.outcomeCode,
                                workItemId: action.workItemId,
                                message: response.message,
                            }),
                        ),
                        catchError((error) => {
                            const failure =
                                outcomesActions.setOutcomeForWorkItemFailure({
                                    outcomeCode: action.outcomeCode,
                                    workItemId: action.workItemId,
                                    error,
                                });

                            if (error.status === 422) {
                                if (
                                    error.error.errorCode === 'RequiresComment'
                                ) {
                                    return of(
                                        outcomesActions.setShowRequireComment({
                                            outcomeCode: action.outcomeCode,
                                            workItemId: action.workItemId,
                                        }),
                                        failure,
                                    );
                                }

                                if (error.error.errorCode === 'RequiresForm') {
                                    return of(
                                        outcomesActions.setShowRequireForm({
                                            outcomeCode: action.outcomeCode,
                                            workItemId: action.workItemId,
                                        }),
                                        failure,
                                    );
                                }
                            }

                            return of(failure);
                        }),
                    ),
            ),
        ),
    );

    setShowRequireComment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(outcomesActions.setShowRequireComment),
            switchMap((action) => {
                this.workItemDialogService.open();

                return this.workItemDialogService.submitForm$.pipe(
                    map(() =>
                        outcomesActions.setOutcomeForWorkItem({
                            outcomeCode: action.outcomeCode,
                            workItemId: action.workItemId,
                            comment: `${this.workItemDialogService?.formValue?.comment} - [${this.workItemDialogService?.formValue?.reason}]`,
                        }),
                    ),
                );
            }),
        ),
    );

    setShowRequireForm$ = createEffect(() =>
        this.actions$.pipe(
            ofType(outcomesActions.setShowRequireForm),
            switchMap((action) => {
                const correlationId =
                    action.correlationId ??
                    `${action.workItemId}-${Date.now()}`;
                const modalRef =
                    this.dynamicFormsManagementService.openDynamicFormModal({
                        formId: WORK_ITEM_FORM_ID,
                        entityId: action.workItemId,
                        dataOverrides: action.dataOverrides,
                        correlationId,
                    });

                type ModalResult = {
                    type: 'modalClosed' | 'formSave';
                    event?: FormFrameworkEvent;
                    closeType?: string;
                };

                const modalClosed$: Observable<ModalResult> = merge(
                    modalRef.dismissed,
                    modalRef.closed,
                ).pipe(
                    map((result) => ({
                        type: 'modalClosed',
                        closeType: result,
                    })),
                );

                return race(
                    modalClosed$,
                    this.dynamicFormsFacade
                        .getFormSaveEvents$([WORK_ITEM_FORM_ID])
                        .pipe(
                            filter(
                                (event) =>
                                    event.action.correlationId ===
                                        correlationId &&
                                    [
                                        FormFrameworkEventType.SaveFailed,
                                        FormFrameworkEventType.SaveSuccess,
                                    ].includes(event.type),
                            ),
                            map(
                                (event) =>
                                    ({
                                        type: 'formSave',
                                        event,
                                    }) as ModalResult,
                            ),
                        ),
                ).pipe(
                    map(
                        ({ type, event }) =>
                            type === 'formSave' &&
                            event.type === FormFrameworkEventType.SaveSuccess,
                    ),
                    tap(
                        (isSuccess) =>
                            isSuccess &&
                            this.modalManagementService.closeActiveModal(),
                    ),
                    map((isSuccess) => {
                        if (isSuccess) {
                            return outcomesActions.setShowRequireFormResult({
                                success: true,
                                correlationId,
                            });
                        }
                        return outcomesActions.setShowRequireFormResult({
                            success: false,
                            correlationId,
                        });
                    }),
                );
            }),
        ),
    );

    setOutcomeForWorkItemSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(outcomesActions.setOutcomeForWorkItemSuccess),
            map((action) =>
                operationsActions.getOperationsForId({
                    entityType: SystemEntity.Activity,
                    entityId: action.workItemId,
                }),
            ),
        ),
    );
}
